import React, { useEffect, useState, useCallback } from "react";
import {
  Mail, CheckCircle2, AlertTriangle, Send, Eye, MousePointerClick,
  ShieldOff, RefreshCw, Search, Inbox, Filter,
} from "lucide-react";
import api from "../lib/api";

const KIND_LABELS = {
  order_confirmation: "Order Confirmation",
  dispatch: "Dispatch Notification",
  newsletter_welcome: "Newsletter Welcome",
  notify_confirmation: "Notify-Me Waitlist",
  restock_alert: "Restock Alert",
  affiliate_invite: "Affiliate Invite",
  reorder_reminder: "Reorder Reminder",
  campaign: "Marketing Campaign",
  contact_copy: "Contact Form (Internal)",
  other: "Other",
};

const STATUS_COLORS = {
  sent: { bg: "rgba(0,194,214,0.12)", border: "#00C2D6", text: "#5EEAD4", label: "Sent" },
  delivered: { bg: "rgba(166,255,0,0.12)", border: "#A6FF00", text: "#A6FF00", label: "Delivered" },
  opened: { bg: "rgba(166,255,0,0.18)", border: "#A6FF00", text: "#A6FF00", label: "Opened" },
  clicked: { bg: "rgba(166,255,0,0.25)", border: "#A6FF00", text: "#A6FF00", label: "Clicked" },
  failed: { bg: "rgba(239,68,68,0.12)", border: "#EF4444", text: "#FCA5A5", label: "Failed" },
  bounced: { bg: "rgba(239,68,68,0.12)", border: "#EF4444", text: "#FCA5A5", label: "Bounced" },
  complained: { bg: "rgba(239,68,68,0.18)", border: "#EF4444", text: "#FCA5A5", label: "Complaint" },
  mocked: { bg: "rgba(156,168,184,0.12)", border: "#9CA8B8", text: "#9CA8B8", label: "Mocked" },
};

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.sent;
  return (
    <span
      className="pg-mono text-[9.5px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-sm whitespace-nowrap"
      style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}
    >
      {c.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="pg-card p-4">
      <div className="flex items-center gap-2.5 mb-2">
        <Icon size={14} style={{ color: accent }} />
        <div className="pg-micro">{label}</div>
      </div>
      <div className="text-[#F7FAFC] text-2xl font-bold pg-display" style={{ fontFamily: "'Exo 2'" }}>
        {value}
      </div>
    </div>
  );
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminEmailActivity() {
  const [data, setData] = useState({ rows: [], total: 0, stats: {}, by_kind_30d: [] });
  const [loading, setLoading] = useState(false);
  const [filterKind, setFilterKind] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [q, setQ] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterKind) params.set("kind", filterKind);
      if (filterStatus) params.set("status", filterStatus);
      if (q.trim()) params.set("q", q.trim());
      params.set("limit", "300");
      const r = await api.get(`/admin/emails?${params.toString()}`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [filterKind, filterStatus, q]);

  useEffect(() => { refresh(); }, [refresh]);

  const stats = data.stats || {};
  const sentish = (stats.sent || 0) + (stats.delivered || 0) + (stats.opened || 0) + (stats.clicked || 0);
  const opened = (stats.opened || 0) + (stats.clicked || 0);
  const failed = (stats.failed || 0) + (stats.bounced || 0) + (stats.complained || 0);

  return (
    <div className="space-y-6" data-testid="admin-email-activity-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="pg-micro mb-2">// Admin · Transactional &amp; marketing email log</div>
          <h1 className="text-[#F7FAFC] text-3xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
            Email activity
          </h1>
          <p className="text-[#9CA8B8] text-sm mt-2 max-w-3xl leading-relaxed">
            Every email Peptigen Labs sends is logged here. Order confirmations, dispatch
            notifications, marketing campaigns and post-purchase follow-ups all land in one feed.
            Configure Resend webhooks to populate the open/click columns.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="pg-btn-ghost text-[10px] tracking-[0.18em] uppercase"
          data-testid="email-activity-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Send} label="Total emails" value={data.total} accent="#5EEAD4" />
        <StatCard icon={CheckCircle2} label="Sent / delivered" value={sentish} accent="#A6FF00" />
        <StatCard icon={Eye} label="Opened / clicked" value={opened} accent="#A6FF00" />
        <StatCard icon={AlertTriangle} label="Failed / bounced" value={failed} accent="#EF4444" />
      </div>

      {/* Filters */}
      <div className="pg-card p-4 flex flex-wrap items-center gap-3">
        <Filter size={14} className="text-[#5EEAD4]" />
        <select
          value={filterKind}
          onChange={(e) => setFilterKind(e.target.value)}
          className="pg-input text-sm py-1.5"
          data-testid="email-activity-filter-kind"
        >
          <option value="">All kinds</option>
          {Object.entries(KIND_LABELS).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="pg-input text-sm py-1.5"
          data-testid="email-activity-filter-status"
        >
          <option value="">All statuses</option>
          {Object.keys(STATUS_COLORS).map((s) => (
            <option key={s} value={s}>{STATUS_COLORS[s].label}</option>
          ))}
        </select>
        <div className="flex-1 min-w-[200px] flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0B1220] border border-[#1E2E44]">
          <Search size={12} className="text-[#9CA8B8]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search recipient or subject..."
            className="bg-transparent outline-none text-sm text-[#F7FAFC] placeholder:text-[#9CA8B8] flex-1"
            data-testid="email-activity-search"
          />
        </div>
      </div>

      {/* By-kind 30d sparkline */}
      {data.by_kind_30d?.length > 0 && (
        <div className="pg-card p-4">
          <div className="pg-micro mb-3">// Last 30 days · by kind</div>
          <div className="flex flex-wrap gap-2">
            {data.by_kind_30d.map((k) => (
              <button
                type="button"
                key={k.kind}
                onClick={() => setFilterKind(k.kind === filterKind ? "" : k.kind)}
                className="px-3 py-1.5 rounded-sm border text-[11px] flex items-center gap-2"
                style={{
                  borderColor: filterKind === k.kind ? "#A6FF00" : "#1E2E44",
                  background: filterKind === k.kind ? "rgba(166,255,0,0.08)" : "transparent",
                  color: "#F7FAFC",
                  cursor: "pointer",
                }}
                data-testid={`email-activity-kind-chip-${k.kind}`}
              >
                <span>{KIND_LABELS[k.kind] || k.kind}</span>
                <span className="pg-mono text-[10px] text-[#5EEAD4]">{k.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Log table */}
      <div className="pg-card overflow-hidden">
        {data.rows.length === 0 ? (
          <div className="p-12 text-center text-[#9CA8B8]">
            <Inbox size={32} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">No emails logged yet matching your filters.</p>
            <p className="text-[12px] mt-2 max-w-md mx-auto">
              Once your first customer triggers an email (or you fire a campaign), entries appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1E2E44] bg-[#0B1220]">
                  <th className="text-left p-3 pg-micro font-normal">When</th>
                  <th className="text-left p-3 pg-micro font-normal">Kind</th>
                  <th className="text-left p-3 pg-micro font-normal">To</th>
                  <th className="text-left p-3 pg-micro font-normal">Subject</th>
                  <th className="text-left p-3 pg-micro font-normal">Status</th>
                  <th className="text-left p-3 pg-micro font-normal">Activity</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.id} className="border-b border-[#1E2E44]/60 hover:bg-[#0B1220]/50">
                    <td className="p-3 text-[#9CA8B8] text-[12px] pg-mono whitespace-nowrap">
                      {fmtTs(row.sent_at)}
                    </td>
                    <td className="p-3 text-[#F7FAFC] text-[12.5px] whitespace-nowrap">
                      {KIND_LABELS[row.kind] || row.kind}
                    </td>
                    <td className="p-3 text-[#F7FAFC] text-[12.5px]">
                      <span className="font-mono">{(row.to || []).join(", ")}</span>
                    </td>
                    <td className="p-3 text-[#9CA8B8] text-[12.5px] max-w-[320px] truncate" title={row.subject}>
                      {row.subject}
                    </td>
                    <td className="p-3">
                      <StatusPill status={row.status} />
                      {row.error && (
                        <div className="text-[10px] text-[#FCA5A5] mt-1 max-w-[200px] truncate" title={row.error}>
                          {row.error}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 text-[10px] text-[#9CA8B8] pg-mono">
                        {row.delivered_at && (
                          <span title={`Delivered ${fmtTs(row.delivered_at)}`}>
                            <CheckCircle2 size={11} className="text-[#A6FF00] inline" />
                          </span>
                        )}
                        {row.opened_at && (
                          <span title={`Opened ${fmtTs(row.opened_at)}`}>
                            <Eye size={11} className="text-[#A6FF00] inline" />
                          </span>
                        )}
                        {row.clicked_at && (
                          <span title={`Clicked ${fmtTs(row.clicked_at)}`}>
                            <MousePointerClick size={11} className="text-[#A6FF00] inline" />
                          </span>
                        )}
                        {row.bounced_at && (
                          <span title={`Bounced ${fmtTs(row.bounced_at)}`}>
                            <ShieldOff size={11} className="text-[#EF4444] inline" />
                          </span>
                        )}
                        {!row.delivered_at && !row.opened_at && !row.bounced_at && (
                          <span className="opacity-50">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Webhook setup hint */}
      <div className="pg-card p-4 border border-[#00C2D6]/30 bg-[#00C2D6]/[0.04]">
        <div className="flex items-start gap-3">
          <Mail size={16} className="text-[#5EEAD4] mt-0.5" />
          <div className="text-[12.5px] text-[#9CA8B8] leading-relaxed">
            <strong className="text-[#F7FAFC]">Tip:</strong> to populate <em>Delivered / Opened / Clicked</em>
            {" "}columns, configure a Resend webhook pointing at{" "}
            <code className="text-[#5EEAD4] font-mono">{`${(process.env.REACT_APP_BACKEND_URL || "")}/api/email-webhook`}</code>
            {" "}in your Resend dashboard. Without the webhook, only the <em>sent / failed</em> state is recorded.
          </div>
        </div>
      </div>
    </div>
  );
}
